import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, TrendingUp, Mail, Calendar, Shield, Zap, MousePointerClick } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const { t } = useLanguage();

  const testimonials = [
    {
      ...t.testimonials.testimonial1,
      image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzkyODQzNDl8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      ...t.testimonials.testimonial2,
      image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzkyODQzNDl8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      ...t.testimonials.testimonial3,
      image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzkyODQzNDl8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      ...t.testimonials.testimonial4,
      image: 'https://images.unsplash.com/photo-1581065178047-8ee15951ede6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzkyODQzNDl8MA&ixlib=rb-4.1.0&q=85'
    },
    {
      ...t.testimonials.testimonial5,
      image: 'https://images.unsplash.com/photo-1580894732444-8ecded7900cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHBlcnNvbnxlbnwwfHx8fDE3NzkyOTQ3OTF8MA&ixlib=rb-4.1.0&q=85'
    }
  ];

  const whyChooseFeatures = [
    { icon: TrendingUp, ...t.whyChoose.feature1 },
    { icon: Calendar, ...t.whyChoose.feature2 },
    { icon: Shield, ...t.whyChoose.feature3 },
    { icon: CheckCircle2, ...t.whyChoose.feature4 },
    { icon: Zap, ...t.whyChoose.feature5 },
    { icon: MousePointerClick, ...t.whyChoose.feature6 }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.92), rgba(255, 255, 255, 0.92)), url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxkYXRhJTIwYW5hbHlzaXN8ZW58MHx8fHwxNzc5Mjk0Nzg1fDA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            {t.hero.title}
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-4 font-medium">
            {t.hero.subtitle}
          </p>
          <p className="text-lg text-gray-600 mb-6">
            {t.hero.description}
          </p>
          <p className="text-xl font-semibold text-gray-900 mb-8">
            {t.hero.tagline}
          </p>
          <Link to="/nos-offres">
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg rounded-md font-semibold transition-all duration-300 hover:scale-105 shadow-lg">
              {t.hero.cta}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Section 1 - Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t.section1.title}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {t.section1.subtitle}
              </p>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{t.section1.feature1}</p>
                </div>
                <div className="flex gap-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{t.section1.feature2}</p>
                </div>
                <div className="flex gap-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{t.section1.feature3}</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxkYXRhJTIwYW5hbHlzaXN8ZW58MHx8fHwxNzc5Mjk0Nzg1fDA&ixlib=rb-4.1.0&q=85"
                alt="Analytics Dashboard"
                className="rounded-lg shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t.testimonials.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t.testimonials.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 italic">"{testimonial.text}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Email Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t.emailSection.title}
              </h2>
              <p className="text-lg text-gray-600">
                {t.emailSection.description}
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7568298/pexels-photo-7568298.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Email on device"
                className="rounded-lg shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t.whyChoose.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {t.whyChoose.subtitle}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {whyChooseFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-6">
                    <Icon className="w-12 h-12 text-emerald-500 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center">
            <Link to="/nos-offres">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg rounded-md font-semibold transition-all duration-300 hover:scale-105 shadow-lg">
                {t.whyChoose.cta}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t.stats.title}
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              {t.stats.subtitle}
            </p>
          </div>
          <div className="relative mb-12">
            <img
              src="https://images.unsplash.com/photo-1524661135-423995f22d0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHx3b3JsZCUyMG1hcHxlbnwwfHx8fDE3NzkyOTQ3ODV8MA&ixlib=rb-4.1.0&q=85"
              alt="World map"
              className="w-full max-w-4xl mx-auto opacity-50 rounded-lg"
            />
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-emerald-400 mb-2">
                {t.stats.stat1.value}
              </div>
              <p className="text-gray-300">{t.stats.stat1.label}</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-emerald-400 mb-2">
                {t.stats.stat2.value}
              </div>
              <p className="text-gray-300">{t.stats.stat2.label}</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-emerald-400 mb-2">
                {t.stats.stat3.value}
              </div>
              <p className="text-gray-300">{t.stats.stat3.label}</p>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-emerald-400 mb-2">
                {t.stats.stat4.value}
              </div>
              <p className="text-gray-300">{t.stats.stat4.label}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t.finalCta.title}
          </h2>
          <p className="text-xl font-semibold text-gray-700 mb-4">
            {t.finalCta.subtitle}
          </p>
          <p className="text-lg text-gray-600 mb-8">
            {t.finalCta.description}
          </p>
          <div className="bg-gray-50 rounded-lg p-8 inline-block shadow-lg">
            <div className="text-5xl font-bold text-gray-900 mb-2">
              {t.testOffer.currency}{t.testOffer.amount}
            </div>
            <Button className="mt-6 bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg rounded-md font-semibold transition-all duration-300 hover:scale-105">
              {t.finalCta.cta}
            </Button>
          </div>
          <div className="mt-6">
            <Link to="/nos-offres" className="text-emerald-600 hover:text-emerald-700 font-medium transition-colors">
              {t.finalCta.seeOffers} <ArrowRight className="inline w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;